import * as React from "react";
import {
  addDmsParts,
  removeAllDmsParts,
  useGetNotSyncedDmsPartsMutation,
  addBulkMatchedParts,
} from "../../../redux/reducer/features/Integration/DmsSetupSlice";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../redux/reducer/rootReducer";
import { LocationPart } from "../../../models/features/Integration/DmsSetup";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  debounce,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ServerAppGrid from "../../app/ServerAppGrid";
import {
  GridColDef,
  GridFilterInputValueProps,
  GridFilterItem,
  GridFilterOperator,
  GridRenderCellParams,
  GridValueGetterParams,
} from "@mui/x-data-grid";
import { AppFilter } from "../../../models/app/AppModels";
import { useGetDmsManufacturersQuery } from "../../../redux/reducer/features/ShoppingPortal/ShoppingPortalSlice";
import { DmsPartRequestDto } from "../../../models/features/ShoppingPortal/DmsPart";
import { AppGridId } from "../../../models/app/AppGridModels";
import {
  capitalizeFirstLetter,
  GetAppGridColumns,
} from "../../../services/utilities/utility";
import { setHasUnsavedChanges } from "../../../redux/reducer/features/App/AppGridSlice";
import { DmsManufacturer } from "../../../models/features/ShoppingPortal/DmsManufacturer";
import TruncatedCellWithTooltip from "../../common/TruncatedCellWithTooltip";

interface DmsPartsProps {
  handleNext: any;
}

const DmsParts: React.FunctionComponent<DmsPartsProps> = ({ handleNext }) => {
  const [stockingFlag, setStockingFlag] = React.useState(true);
  const [dmsPartsManufacturers, setDMSPartsManufacturers] = React.useState<
    DmsManufacturer[]
  >([]);
  const [selectedManufacturers, setSelectedManufacturers] = React.useState<
    DmsManufacturer[]
  >([]);
  const PartgridSetting = useSelector((state: AppState) =>
    state?.AppGridReducer?.setting.find(
      (x) => x.gridId === AppGridId.Initiate_Parts_DMS_Parts,
    ),
  );
  const selectedProducts = useSelector(
    (state: AppState) => state.dmsReducer.dmsParts,
  );
  const locations = useSelector(
    (state: AppState) => state.authReducer.dmsSetup?.dmsLocations,
  );
  const { data: dmsManufacturersList } = useGetDmsManufacturersQuery({
    dealerId: locations ? (locations[0].dealerId.toString() ?? "") : "",
  });

  const dispatch = useDispatch();
  const [totalRecords, setTotalRecords] = React.useState(0);
  const [paging, setPaging] = React.useState<AppFilter>({
    pageIndex: 0,
    pageSize: 100,
    totalRecords: 0,
    items: [],
    quickFilterValues: [],
    virtualShowroomProducts: false,
  });
  const [getNotSyncedDmsPartsMutation, getNotSyncedDmsPartsMutationResult] =
    useGetNotSyncedDmsPartsMutation();
  const [dmsParts, setDmsParts] = React.useState<LocationPart[]>([]);
  const { dmsSetup } = useSelector((state: AppState) => state.authReducer);
  React.useEffect(() => {
    fetchDmsPartsData();
  }, [paging, selectedManufacturers]);

  React.useEffect(() => {
    setPaging({ ...paging, pageIndex: 0 });
  }, [paging.items, paging.quickFilterValues]);

  React.useEffect(() => {
    if (
      dmsManufacturersList?.entityList &&
      dmsManufacturersList?.entityList.length > 0
    ) {
      let mfrs =
        dmsManufacturersList?.entityList
          .filter((x) => x.isPartManufacturer)
          .sort((a, b) => a.name.localeCompare(b.name)) ?? [];
      setDMSPartsManufacturers(mfrs);
      setSelectedManufacturers(mfrs);
    }
  }, [dmsManufacturersList]);

  const fetchDmsPartsData = () => {
    var mfgFilter: string[] = [];
    var inventoryPartId = "";
    var dmsMfrId = "";
    if (paging.items.some((n) => n.columnField === "inventoryPartId")) {
      inventoryPartId =
        paging.items.find((n) => n.columnField === "inventoryPartId")?.value ??
        "";
    }

    if (paging.items.some((n) => n.columnField === "mfrName")) {
      dmsMfrId =
        paging.items.find((n) => n.columnField === "mfrName")?.value ?? "";
    }

    if (selectedManufacturers.length > 0) {
      if (selectedManufacturers.length === dmsPartsManufacturers.length) {
        mfgFilter = [];
      } else {
        mfgFilter = selectedManufacturers.map((n) => n.code);
      }
    }

    var data: DmsPartRequestDto = {
      dealerIds:
        dmsSetup?.dmsLocations
          .filter((n) => n.storeLocationId > 0)
          .map((n) => n.dealerId.toString()) ?? [],
      pageSize: paging.pageSize,
      pageNumber: paging.pageIndex,
      stockingOnly: stockingFlag ? true : false,
      searchPartNumber: [],
      searchPartDescription: "",
      search: paging.quickFilterValues.join(" "),
      searchManufacturerId: dmsMfrId,
      searchManufacturerCode: mfgFilter.length > 0 ? mfgFilter : [],
      searchManufacturerCodeNotIn: [],
      inventoryPartIdNotIn: [],
      searchPartId: [inventoryPartId],
    };
    getNotSyncedDmsPartsMutation({ data: data }).then((response: any) => {
      if (response?.data && response?.data?.entityList !== null) {
        setDmsParts(response?.data?.entityList);
        if (paging.pageIndex === 0) {
          setTotalRecords(response.data.totalRecords);
        }
      }
    });
  };
  const handleItemSelect = (items: any[]) => {
    if (items.length === 0) {
      dispatch(addBulkMatchedParts([]));
    }
    dispatch(setHasUnsavedChanges(items.length > 0));
    const updatedSelectedProducts =
      selectedProducts?.filter((sp) => items.includes(sp.inventoryPartId)) ??
      [];

    dmsParts.forEach((item) => {
      if (
        items.includes(item.inventoryPartId) &&
        !selectedProducts?.some(
          (sp) => sp.inventoryPartId === item.inventoryPartId,
        )
      ) {
        updatedSelectedProducts.push(item);
      }
    });
    dispatch(
      addDmsParts({
        parts: updatedSelectedProducts,
      }),
    );
  };

  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setStockingFlag(!stockingFlag);
    setPaging((prev) => ({ ...prev, pageIndex: 0 }));
    dispatch(removeAllDmsParts());
  };

  const onManufacturerChange = (value: DmsManufacturer[]) => {
    setSelectedManufacturers(value);
    dispatch(removeAllDmsParts());
  };

  const handlePartNext = () => {
    dispatch(
      addBulkMatchedParts(
        selectedProducts?.map((n) => ({
          dmsPart: n,
          isManuallyMatched: true,
        })) ?? [],
      ),
    );
    handleNext();
  };
  function CustomFilterInputValue(props: GridFilterInputValueProps) {
    const { item, applyValue } = props;
    const customFilterRef: React.Ref<any> = React.useRef(null);
    const [inputValue, setInputValue] = React.useState(item.value || "");

    const handleSearchTextChange = React.useCallback(
      debounce((newValue: string) => {
        applyValue({ ...item, value: newValue });
      }, 1000),
      [applyValue, item],
    );

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      setInputValue(newValue);
      handleSearchTextChange(newValue);
    };

    React.useEffect(() => {
      setInputValue(item.value || "");
    }, [item.value]);
    return (
      <TextField
        name="mfrFilterValue"
        onChange={handleInputChange}
        ref={customFilterRef}
        size="small"
        value={inputValue}
        variant="standard"
        label="Value"
        sx={{ pt: 0.4 }}
      ></TextField>
    );
  }
  const customFilterOperators: GridFilterOperator[] = [
    {
      label: "Equals",
      value: "equals",
      getApplyFilterFn: (filterItem: GridFilterItem) => {
        if (
          !filterItem.columnField ||
          !filterItem.value ||
          !filterItem.operatorValue
        ) {
          return null;
        }

        return (params): boolean => {
          return params.value == filterItem.value;
        };
      },
      InputComponent: CustomFilterInputValue,
    },
  ];

  function CustomMfrNameFilterInputValue(props: GridFilterInputValueProps) {
    const { item, applyValue } = props;

    const handleDropdownFilterChange = (event: any, newValue: any) => {
      applyValue({
        ...item,
        value: newValue ? newValue.manufacturerId : undefined,
      });
    };
    return (
      <Autocomplete
        options={dmsPartsManufacturers ?? []}
        getOptionLabel={(mfr) =>
          `${capitalizeFirstLetter(mfr.name !== null ? mfr.name : "")}`
        }
        value={
          dmsPartsManufacturers.find(
            (mfr) => mfr.manufacturerId === item.value,
          ) || null
        }
        onChange={handleDropdownFilterChange}
        renderInput={(params) => (
          <TextField
            {...params}
            name="mfrFilterValue"
            size="small"
            variant="standard"
            label="Manufacturer"
            sx={{ pt: 0.4 }}
          />
        )}
        PaperComponent={({ children }) => (
          <Box
            sx={{
              width: 320,
              backgroundColor: "white",
              boxShadow: 3,
              borderRadius: 1,
              mt: 1,
              "& .MuiAutocomplete-listbox": {
                maxHeight: 300,
                overflow: "auto",
              },
            }}
          >
            {children}
          </Box>
        )}
      />
    );
  }

  const customMfrNameFilterOperator: GridFilterOperator[] = [
    {
      label: "Equals",
      value: "equals",
      getApplyFilterFn: (filterItem: GridFilterItem) => {
        if (
          !filterItem.columnField ||
          !filterItem.value ||
          !filterItem.operatorValue
        ) {
          return null;
        }

        return (params): boolean => {
          return params.value == filterItem.value;
        };
      },
      InputComponent: CustomMfrNameFilterInputValue,
    },
  ];

  const columns: GridColDef[] = [
    {
      field: "mfrName",
      headerName: "Manufacturer",
      flex: 1,
      filterOperators: customMfrNameFilterOperator,
      renderCell: (params: GridRenderCellParams) => {
        return <TruncatedCellWithTooltip value={params.row.mfrName} />;
      },
    },
    {
      field: "partNumber",
      headerName: "Part Number",
      flex: 1,
      filterable: false,
      renderCell: (params: GridRenderCellParams<any>) => {
        return <TruncatedCellWithTooltip value={params.row.partNumber} />;
      },
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Tooltip title={params.row.description}>
            <Typography
              className="truncate-ellipsis"
              sx={{ fontSize: "0.875rem" }}
            >
              {params.row.description}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: "averageCost",
      headerName: "Cost ($)",
      headerAlign: "right",
      flex: 1,
      align: "right",
      type: "number",
      filterable: false,
      valueGetter: (params: GridValueGetterParams<any>) =>
        params.row.averageCost.toFixed(2),
    },
    {
      field: "price",
      headerName: "MSRP ($)",
      headerAlign: "right",
      flex: 1,
      type: "number",
      filterable: false,
      align: "right",
      valueGetter: (params: GridValueGetterParams<any>) =>
        params.row.price.toFixed(2),
    },
    {
      field: "availableQty",
      headerName: "Stock",
      flex: 1,
      type: "number",
      filterable: false,
    },
  ];

  return (
    <>
      <Box
        display={"flex"}
        justifyContent={"space-between"}
        alignItems={"center"}
        gap={2}
        p={0.5}
      >
        <Alert severity="info">
          Please select parts you want to sync with the website.
        </Alert>
        <Box>
          <Button
            size="small"
            variant="contained"
            disabled={selectedProducts?.length === 0}
            onClick={handlePartNext}
          >
            Next
          </Button>
        </Box>
      </Box>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <ServerAppGrid
          gridId={AppGridId.Initiate_Parts_DMS_Parts}
          getRowId={(row) => row.inventoryPartId}
          data={dmsParts}
          columns={GetAppGridColumns(
            columns,
            PartgridSetting?.columnVisibilityModel,
          )}
          isLoading={getNotSyncedDmsPartsMutationResult.isLoading}
          paging={paging}
          setPaging={setPaging}
          totalRecords={totalRecords}
          height="60vh"
          selectedRows={selectedProducts?.map((n) => n.inventoryPartId)}
          onSelect={(newSelection: any) => {
            handleItemSelect(newSelection);
          }}
          keepNonExistentRowsSelected={true}
          hidePageSize={true}
          customToolbar={true}
          switchLabel="In-stock"
          switchChecked={stockingFlag}
          onSwitchChange={handleSwitchChange}
          manufacturerOptions={dmsPartsManufacturers}
          selectedManufacturers={selectedManufacturers}
          onManufacturerChange={onManufacturerChange}
        ></ServerAppGrid>
      </Box>
    </>
  );
};

export default DmsParts;
